/* ==========================================================================
   Header height fix — U&S Clothing
   assets/header-fixes.js

   The blank strip that appears under the header, and stays there after the
   mega menu closes.

   sticky-header.js measures the header and writes the result into
   --sticky-header-height-desktop / -mobile, which .section-header then uses
   as its min-height. A mega menu lives INSIDE the header, so any measurement
   taken while one is open records the header plus the whole open panel. That
   inflated number is then stuck as a min-height, and the gap remains once the
   menu closes. It only happens when a measurement and an open menu coincide,
   which is why it looked random.

   This wraps the theme's own measure call so it declines to run while a menu
   is open, and re-runs it once the menu has closed. Nothing in the theme is
   edited, and removing this file returns everything to its previous state.
   ========================================================================== */
(function () {
  'use strict';

  function aMenuIsOpen() {
    return !!document.querySelector(
      '.header__inline-menu details[open], ' +
      '.header__inline-menu details.closing, ' +
      '.section-header.menu-open'
    );
  }

  var stickyEl = null;
  var originalResize = null;

  function measureNow() {
    if (!stickyEl || !originalResize) return;
    if (aMenuIsOpen()) return;
    stickyEl.preventResize = false;
    originalResize();
  }

  function patch() {
    var el = document.querySelector('sticky-header');
    if (!el || el.__headerFixApplied) return !!el;
    if (typeof el.onWindowResize !== 'function') return false;

    stickyEl = el;
    originalResize = el.onWindowResize.bind(el);
    el.__headerFixApplied = true;

    /* Every caller goes through here — the theme's own resize handler, and
       anything else that asks for a re-measure. */
    el.onWindowResize = function () {
      if (aMenuIsOpen()) return;
      originalResize();
    };

    return true;
  }

  /* ------------------------------------------------------------------
     The header that stays stuck at the top of the page

     Scrolling up fires a burst of scroll events. Each queues work in a
     requestAnimationFrame: some call reveal(), which adds
     shopify-section-header-sticky, and the last calls reset(), which removes
     it. Those frames do not always run in the order they were queued, so a
     reveal can land after the reset and the class goes straight back on.

     The page is then sitting at the top with the header still in its
     collapsed fixed state, leaving its 126px placeholder as a blank strip
     above the content. Any later scroll or resize clears it, which is why it
     looked random and why it "fixed itself after a few seconds".

     Reproduced by scrolling down and then quickly back to the top: scrollY 0,
     internal currentScrollTop 0, headerBoundTop 165 — reset should have run,
     and the class was still there.

     This watches for that exact state and reruns the theme's own reset.
     ------------------------------------------------------------------ */
  function stuckAtTop() {
    if (!stickyEl) return false;
    var header = stickyEl.header || document.querySelector('.section-header');
    if (!header) return false;
    if (!header.classList.contains('shopify-section-header-sticky')) return false;

    /* headerBoundTop can be stale after a mis-timed measurement, so fall
       back to the placeholder's own height rather than trusting it blindly. */
    var bound = typeof stickyEl.headerBoundTop === 'number' && stickyEl.headerBoundTop > 0
      ? stickyEl.headerBoundTop
      : Math.round(header.getBoundingClientRect().height) || 0;
    return (window.scrollY || document.documentElement.scrollTop || 0) <= bound;
  }

  function unstickIfNeeded() {
    if (!stuckAtTop()) return;
    if (typeof stickyEl.reset === 'function') {
      stickyEl.reset();
    } else {
      var header = stickyEl.header || document.querySelector('.section-header');
      header.classList.remove('shopify-section-header-hidden',
                              'shopify-section-header-sticky', 'animate');
    }
  }

  /* Watching the scroll was not enough. On a fast scroll the burst of queued
     frames can land after the scroll has already settled, so the reveal that
     re-adds the class arrives after the check has run — and the header stays
     stuck until some later event nudges it. Opening devtools cleared it,
     which is a resize, and that was the clue.

     So watch the class itself instead. Whenever the sticky class appears
     while the page is at or above the header bound, it is wrong by
     definition, whatever queued it and whenever it ran. Removing it flips
     the condition, so this cannot loop. */
  function watchForStuckHeader() {
    var header = (stickyEl && stickyEl.header) || document.querySelector('.section-header');
    if (!header) return;

    new MutationObserver(function () {
      unstickIfNeeded();
    }).observe(header, { attributes: true, attributeFilter: ['class'] });

    /* and a settle check, for the case where the class was already wrong
       before this started watching */
    var settle = null;
    window.addEventListener('scroll', function () {
      if (settle) clearTimeout(settle);
      settle = setTimeout(function () {
        unstickIfNeeded();
        setTimeout(unstickIfNeeded, 300);
      }, 140);
    }, { passive: true });

    unstickIfNeeded();
  }

  /* ------------------------------------------------------------------
     The stale header height

     calculateHeaderHeight() sums the clientHeight of the sticky-header's
     direct children and writes the total into --sticky-header-height-*,
     which the theme then applies as a min-height.

     Caught in the act: children summed to 126px while the variable said
     345px and min-height was holding the header at 345px. 345 is 48 + 297,
     so the inner <header> measured 297px at that moment instead of its real
     78px. The mega menu panels are display:block / visibility:visible and
     only stay out of the layout because they are positioned — if a
     measurement lands before that positioning settles, they count, and the
     wrong total sticks.

     Nothing clears it afterwards, which is the blank strip under the nav.

     So: compare the stored value against what the children actually add up
     to, and correct it when they disagree. Only while not sticky, because
     the collapsed sticky header is legitimately shorter than the
     placeholder it leaves behind.
     ------------------------------------------------------------------ */
  function realHeaderHeight() {
    var host = document.querySelector('sticky-header');
    if (!host) return 0;
    var total = 0;
    Array.prototype.forEach.call(host.children, function (c) {
      total += c.clientHeight || 0;
    });
    return total;
  }

  function fixStaleHeight() {
    var host = document.querySelector('sticky-header');
    var header = (stickyEl && stickyEl.header) || document.querySelector('.section-header');
    if (!host || !header) return;
    if (header.classList.contains('shopify-section-header-sticky')) return;

    var real = realHeaderHeight();
    if (!real) return;

    var stored = parseInt(
      getComputedStyle(document.body).getPropertyValue('--sticky-header-height-desktop'), 10
    );
    if (!stored) return;

    /* a few pixels of rounding is normal; 20 is not */
    if (Math.abs(stored - real) < 20) return;

    document.body.style.setProperty('--sticky-header-height-desktop', real + 'px');
    document.body.style.setProperty('--sticky-header-height-mobile', real + 'px');

    var inner = host.querySelector('header');
    if (inner) {
      document.body.style.setProperty('--header-height', inner.clientHeight + 'px');
    }

    if (stickyEl) {
      stickyEl.fullHeaderHeight = real;
      if (inner) stickyEl.headerHeight = inner.clientHeight;
    }
  }

  /* Timed checks were not enough. The bad value can be written at any point —
     an app resizing something, lazy content settling, a mega menu measuring
     mid-transition — and once the last timer has run, nothing corrects it.
     Confirmed on a live page: the variable sat at 214px against a real 126px,
     and a single resize event put it right, because the theme's own
     onWindowResize writes the correct number when it runs at a sane moment.

     So watch the variable instead of guessing when it might change. The
     theme writes it with body.style.setProperty, which mutates the style
     attribute, so observing that catches every write whoever makes it.

     Correcting also mutates the attribute, but the values then agree and the
     next pass does nothing, so this cannot loop. */
  function watchForStaleHeight() {
    fixStaleHeight();

    var busy = false;
    new MutationObserver(function () {
      if (busy) return;
      busy = true;
      setTimeout(function () { busy = false; fixStaleHeight(); }, 60);
    }).observe(document.body, { attributes: true, attributeFilter: ['style'] });

    /* the panels settle over the first couple of seconds */
    [300, 800, 1500, 2500, 4000].forEach(function (t) {
      setTimeout(fixStaleHeight, t);
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { setTimeout(fixStaleHeight, 100); });
    }
    window.addEventListener('load', function () { setTimeout(fixStaleHeight, 200); });
    window.addEventListener('resize', function () { setTimeout(fixStaleHeight, 250); });

    document.addEventListener('mouseleave', function (e) {
      if (e.target && e.target.closest && e.target.closest('.header__inline-menu')) {
        setTimeout(fixStaleHeight, 450);
      }
    }, true);
  }

  function boot() {
    watchForStuckHeader();
    watchForStaleHeight();
    if (!patch()) {
      var tries = 0;
      var iv = setInterval(function () {
        if (patch() || ++tries > 40) clearInterval(iv);
      }, 150);
    }

    /* Re-measure once a menu has closed, in case one was open at load and a
       measurement was skipped. */
    document.addEventListener('mouseleave', function (e) {
      if (e.target && e.target.closest && e.target.closest('.header__inline-menu')) {
        setTimeout(measureNow, 420);
      }
    }, true);

    /* the fade-out carries a .closing class for 300ms after the pointer goes */
    document.addEventListener('click', function () { setTimeout(measureNow, 420); }, true);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { setTimeout(measureNow, 80); });
    }
    window.addEventListener('load', function () { setTimeout(measureNow, 150); });
    setTimeout(measureNow, 1600);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();